export type WorkersListPayload = {
  data: any[];
  totalPages: number;
  total: number;
  totalItems?: number;
  isOtherAreasFallback?: boolean;
  emptyAreaMessage?: string;
  requestedServiceName?: string;
  requestedAreaName?: string;
};

const EMPTY_WORKERS: WorkersListPayload = {
  data: [],
  totalPages: 0,
  total: 0,
  totalItems: 0,
};

/** Matches `api.getFilterWorkersList` return shape: `{ data, totalPages, total }`. */
export function normalizeWorkersListResponse(raw: unknown): WorkersListPayload {
  if (raw == null) return { ...EMPTY_WORKERS };

  if (Array.isArray(raw)) {
    return {
      data: raw,
      totalPages: raw.length > 0 ? 1 : 0,
      total: raw.length,
      totalItems: raw.length,
    };
  }

  if (typeof raw !== "object") return { ...EMPTY_WORKERS };

  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.data)) {
    const payload: WorkersListPayload = {
      data: obj.data,
      totalPages: Number(obj.totalPages) || 0,
      total: Number(obj.total ?? obj.totalItems) || 0,
      totalItems: Number(obj.totalItems ?? obj.total) || 0,
    };

    if (obj.isOtherAreasFallback === true) {
      payload.isOtherAreasFallback = true;
      if (typeof obj.emptyAreaMessage === "string" && obj.emptyAreaMessage) {
        payload.emptyAreaMessage = obj.emptyAreaMessage;
      }
      if (typeof obj.requestedServiceName === "string") {
        payload.requestedServiceName = obj.requestedServiceName;
      }
      if (typeof obj.requestedAreaName === "string") {
        payload.requestedAreaName = obj.requestedAreaName;
      }
    }

    return payload;
  }

  if (obj.data && typeof obj.data === "object") {
    return normalizeWorkersListResponse(obj.data);
  }

  return { ...EMPTY_WORKERS };
}

export function normalizeServiceResponse(raw: unknown): any | null {
  if (raw == null) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;

  const obj = raw as Record<string, unknown>;

  if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
    const inner = obj.data as Record<string, unknown>;
    if (inner.name || inner._id || inner.id) return obj.data;
    if (inner.data) return normalizeServiceResponse(obj.data);
  }

  if (obj.name || obj._id || obj.id) return raw;

  return raw;
}

export function getServiceId(service: any): string | undefined {
  if (!service) return undefined;
  const id = service.id ?? service._id;
  return id != null ? String(id) : undefined;
}

type PageContextWithUrl = {
  urlParsed?: { search?: Record<string, string | string[] | undefined> };
  urlOriginal?: string;
};

export function getSsrLang(pageContext: PageContextWithUrl): string {
  const searchLang = pageContext.urlParsed?.search?.lang;
  const langValue = Array.isArray(searchLang) ? searchLang[0] : searchLang;
  if (langValue === "en" || langValue === "es") return langValue;

  const url = pageContext.urlOriginal || "";
  const match = url.match(/[?&]lang=(en|es)/i);
  if (match?.[1]) return match[1].toLowerCase();

  return "es";
}

export function getBackendBaseUrl(): string {
  // @ts-expect-error import.meta env typing in this project
  return (import.meta.env.VITE_BACKEND_URL as string) || "";
}

/** Keep SSR hooks under Vike's default ~4s warning when the API is slow/unreachable. */
export const SSR_AXIOS_TIMEOUT_MS = 3500;

/** Matches `api.getServiceCategories` return shape (array of categories with services). */
export function normalizeServiceCategoriesResponse(raw: unknown): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data;
    if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
      return normalizeServiceCategoriesResponse(obj.data);
    }
  }

  return [];
}
